import { FreeForAllMode } from './FreeForAllMode.js';
import { TeamVsTeamMode } from './TeamVsTeamMode.js';
import { PvEMode } from './PvEMode.js';
import { CastleAttackMode } from './CastleAttackMode.js';
import { SquadMode } from './SquadMode.js';

export class GameModeManager {
  constructor() {
    this.modes = {
      'free-for-all': new FreeForAllMode(),
      'team-vs-team': new TeamVsTeamMode(),
      'pve': new PvEMode(),
      'castle-attack': new CastleAttackMode(),
      'squad': new SquadMode()
    };
    
    this.currentMode = null;
    this.selectedMode = 'random'; // Default to random
  }

  // Get available mode names for UI
  getAvailableModes() {
    return [
      { id: 'random', name: 'Random Mode', description: 'Randomly selects a game mode each match' },
      { id: 'free-for-all', name: 'Free-for-All', description: 'Everyone fights everyone - last player standing wins' },
      { id: 'team-vs-team', name: 'Team vs Team', description: 'Teams battle for supremacy' },
      { id: 'pve', name: 'Players vs AI', description: 'Players cooperate against AI enemies' },
      { id: 'castle-attack', name: 'Castle Attack', description: 'Teams race to destroy the central castle' },
      { id: 'squad', name: 'Squad Battle', description: 'Small team battles (2v2, 3v3, 4v4, 5v5)' }
    ];
  }

  // Set the selected mode (called from UI)
  setSelectedMode(modeId) {
    const prevSelected = this.selectedMode;
    this.selectedMode = modeId;
    console.log('[GameModeManager] setSelectedMode:', { previous: prevSelected, next: modeId });
    // Reset current mode so it can be re-selected for random mode
    this.currentMode = null;
  }

  // Get the actual mode to use for this match
  getCurrentMode() {
    // If we already have a current mode, but selectedMode changed to a non-random value,
    // switch to the explicitly selected mode to honor user choice.
    if (this.currentMode) {
      const currentModeId = Object.keys(this.modes).find(key => this.modes[key] === this.currentMode);
      if (this.selectedMode && this.selectedMode !== 'random' && this.selectedMode !== currentModeId) {
        this.currentMode = this.modes[this.selectedMode];
        console.log('[GameModeManager] getCurrentMode switch to explicit selection:', { modeId: this.selectedMode });
        return { mode: this.currentMode, modeId: this.selectedMode };
      }
      console.log('[GameModeManager] getCurrentMode (sticky):', { modeId: currentModeId });
      return { mode: this.currentMode, modeId: currentModeId };
    }
    
    if (this.selectedMode === 'random') {
      // Randomly select a mode (excluding random itself)
      const modeIds = Object.keys(this.modes);
      const randomIndex = Math.floor(Math.random() * modeIds.length);
      const randomModeId = modeIds[randomIndex];
      this.currentMode = this.modes[randomModeId];
      console.log('[GameModeManager] getCurrentMode picked RANDOM:', { modeId: randomModeId });
      return { mode: this.currentMode, modeId: randomModeId };
    } else {
      this.currentMode = this.modes[this.selectedMode];
      console.log('[GameModeManager] getCurrentMode picked EXPLICIT:', { modeId: this.selectedMode });
      return { mode: this.currentMode, modeId: this.selectedMode };
    }
  }

  // Initialize the current mode
  initializeMode(scene) {
    if (this.currentMode) {
      this.currentMode.initialize(scene);
    }
  }

  // Delegate methods to current mode
  spawnPlayers(scene, players) {
    if (this.currentMode) {
      return this.currentMode.spawnPlayers(scene, players);
    }
  }

  checkCombat(scene) {
    if (this.currentMode) {
      return this.currentMode.checkCombat(scene);
    }
  }

  checkWinCondition(scene) {
    if (this.currentMode) {
      return this.currentMode.checkWinCondition(scene);
    }
  }

  getPlayerTarget(player, allPlayers) {
    if (this.currentMode) {
      return this.currentMode.getPlayerTarget(player, allPlayers);
    }
  }

  // Get mode-specific information for UI
  getModeInfo() {
    if (this.currentMode) {
      return this.currentMode.getInfo();
    }
    return null;
  }
}

